import type { GamePhase } from './GameEvents';

export type RunResult = 'gameover' | 'victory';

/**
 * The legal phase transitions.
 *
 * The rules live here rather than in `Game` so they can be tested without a
 * browser; `Game` performs the effects that go with them.
 */
export const PHASE_TRANSITIONS: Record<GamePhase, readonly GamePhase[]> = {
  menu: ['playing'],
  playing: ['paused', 'menu', 'gameover', 'victory'],
  paused: ['playing', 'menu', 'gameover', 'victory'],
  gameover: ['playing', 'menu'],
  victory: ['playing', 'menu'],
};

/**
 * The game's phase machine.
 *
 * It owns *what* phase the game is in and which phase it may move to next.
 * Everything that happens when the phase changes — the HUD, the menu, the
 * pointer lock, resetting the run — belongs to `Game`.
 */
export class GameSession {
  private current: GamePhase = 'menu';

  get phase(): GamePhase {
    return this.current;
  }

  /** True when the phase may change to `to`. */
  canTransition(to: GamePhase): boolean {
    return this.current === to || PHASE_TRANSITIONS[this.current].includes(to);
  }

  /**
   * Move to a new phase. Returns false when the transition is not legal, so
   * the caller can ignore an end-of-round that arrives outside a round.
   */
  transition(to: GamePhase): boolean {
    if (!this.canTransition(to)) return false;
    this.current = to;
    return true;
  }

  /**
   * Start or restart a run.
   *
   * A restart from a running round is always legal: the restart key has to
   * work while the player is playing.
   */
  begin(): boolean {
    if (this.current === 'playing') return true;
    return this.transition('playing');
  }

  /** Pause a running round. */
  pause(): boolean {
    if (this.current === 'paused') return false;
    return this.transition('paused');
  }

  /** Resume a paused round. */
  resume(): boolean {
    if (this.current === 'playing') return false;
    return this.transition('playing');
  }

  /** Leave the round and return to the main menu. */
  quit(): boolean {
    if (this.current === 'menu') return false;
    return this.transition('menu');
  }

  /** End the round. Only legal while a round is running. */
  finish(result: RunResult): boolean {
    if (this.current === result) return false;
    return this.transition(result);
  }
}
